import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import PropertyCard from "@/components/property/PropertyCard";
import { Link } from "react-router-dom";

/**
 * LongTailLanding — reusable SEO landing page for long-tail location / mood searches.
 *
 * `filter` is applied client-side: a function that decides which seeded properties to surface.
 * Hero image is picked dynamically from the first matching property — so every photo on the
 * site is Vyagram's own.
 */
export default function LongTailLanding({ slug, title, subtitle, description, filter, keywords }) {
  const [properties, setProperties] = useState([]);
  useEffect(() => {
    api.get("/properties").then(({ data }) => setProperties(filter ? data.filter(filter) : data));
  }, [filter]);

  const heroImage = properties[0]?.hero_image;

  return (
    <div data-testid={`landing-${slug}`}>
      <Helmet>
        <title>{`${title} · Vyagram Experiences`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={`https://vyagram.com/${slug}`} />
      </Helmet>

      <section className="relative isolate overflow-hidden">
        {heroImage && <img src={heroImage} alt={title} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 to-noir/70" />
        <div className="relative mx-auto max-w-5xl px-4 py-24 sm:px-6 sm:py-32">
          <p className="font-accent text-base text-gold uppercase tracking-[0.25em]">Stays Guide</p>
          <h1 className="mt-4 font-display text-4xl text-parchment sm:text-5xl lg:text-6xl">{title}</h1>
          <p className="mt-4 max-w-2xl editorial-body !text-parchment/85">{subtitle}</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-10">
        <p className="editorial-body">{description}</p>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-10">
        <h2 className="font-display text-3xl">{properties.length} stays for you</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {properties.map((p) => (<PropertyCard key={p.slug} p={p} />))}
        </div>
        {properties.length === 0 && (
          <div className="rounded-2xl border border-dashed border-forest/20 bg-white/60 p-10 text-center">
            <p className="font-display text-2xl">No stays match this search yet.</p>
            <Link to="/stays" className="vyg-link-gold mt-3 inline-flex">Browse everything →</Link>
          </div>
        )}
      </section>
    </div>
  );
}
